import { confirmAsync, ModalBody, ModalTextLine, ModalTitle, WrappingModalContainer } from '@app/components/Modal';
import RolesModal, { RoleState } from '@app/components/userManagement/organizationUsersTable/RolesModal';
import { OrganizationalUser, withGlobalAppRoleIds } from '@app/domain/userManagement/organizationUsers';
import { UserRoleType } from '@app/domain/userManagement/roleTypes';
import { UpdateWebUserRolesRequest } from '@app/domain/userManagement/users';
import { updateGlobalUserRoles } from '@app/services/userManagement/usersService';
import Loadable from '@app/utils/Loadable';
import { shoot } from '@app/utils/messageLauncher';
import { Alert } from 'antd';
import React, { FC } from 'react';

interface Props {
  organizationalUser: OrganizationalUser;
  globalWebAppRoleTypes: Loadable<UserRoleType[]>;
  onUserUpdated(organizationalUser: OrganizationalUser): void;
  onDone: () => void;
}

// Backee refuses to revoke a role outside its role types, so such a held role stays read-only
function getRoleState(roleId: string, roleType: UserRoleType | undefined): RoleState {
  if (!roleType) {
    return { isDisabled: true, tooltip: 'This role is not in the Backee role list and cannot be changed here' };
  }

  return { isDisabled: false, tooltip: null };
}

const GlobalRolesModal: FC<Props> = ({ organizationalUser, globalWebAppRoleTypes, onUserUpdated, onDone }) => {
  function confirmChange(change: UpdateWebUserRolesRequest): Promise<boolean> {
    return confirmAsync({
      content: (
        <WrappingModalContainer>
          <ModalTitle>Change the global roles of {organizationalUser.name}?</ModalTitle>
          <ModalBody>
            <ModalTextLine>They apply in every organization this user belongs to.</ModalTextLine>
            {change.addedWebAppRoleIds.map((roleId) => (
              <ModalTextLine key={`added-${roleId}`}>+ {roleId}</ModalTextLine>
            ))}
            {change.removedWebAppRoleIds.map((roleId) => (
              <ModalTextLine key={`removed-${roleId}`}>− {roleId}</ModalTextLine>
            ))}
          </ModalBody>
        </WrappingModalContainer>
      ),
      okText: 'YES',
      cancelText: 'NO',
      icon: <span />,
      maskClosable: false,
      width: 550,
    });
  }

  return (
    <RolesModal
      title={<>Global roles - {organizationalUser.name}</>}
      elementIds={{
        card: 'user-global-roles-modal',
        search: 'user-global-roles-search',
        table: 'user-global-roles-table',
        close: 'btn-user-global-roles-modal-close',
        cancel: 'btn-user-global-roles-modal-cancel',
        save: 'btn-user-global-roles-modal-save',
      }}
      contentColumnTitle='Global roles'
      banner={<Alert type='warning' showIcon message='Global roles apply in every organization this user belongs to.' />}
      roleTypes={globalWebAppRoleTypes}
      initialActiveRoleIds={organizationalUser.globalAppRoleIds}
      canEdit
      getRoleState={getRoleState}
      confirmChange={confirmChange}
      saveChange={async (change): Promise<void> => {
        const updatedTenantUser = await updateGlobalUserRoles(organizationalUser.tenantUserId, change);
        onUserUpdated(withGlobalAppRoleIds(organizationalUser, updatedTenantUser.webAppRoleIds[''] ?? []));
        shoot({ type: 'success', closeable: true }, `Global roles of "${organizationalUser.name}" saved`);
      }}
      onDone={onDone}
    />
  );
};

export default GlobalRolesModal;
